import { useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useAuth } from "../../auth/AuthContext";
import { API_BASE } from "../../api/http";
import { electronicInvoicingApi } from "../../api/electronicInvoicingApi";

type ReceivedInvoiceNotification = {
  id: string;
  notificationType: string;
  status: string;
  message?: string | null;
  receivedAt: string;
  payload?: any;
};

type ReceivedInvoiceDetail = {
  id: string;
  provider?: string | null;
  externalId?: string | null;
  documentType?: string | null;
  documentNumber?: string | null;
  documentDate?: string | null;
  supplierName?: string | null;
  supplierVat?: string | null;
  supplierFiscalCode?: string | null;
  total?: number | string | null;
  currency?: string | null;
  status?: string | null;
  xmlFileName?: string | null;
  xmlContent?: string | null;
  payload?: any;
  importedAt?: string | null;
  updatedAt?: string | null;
  notifications?: ReceivedInvoiceNotification[];
};

function formatDate(value?: string | null) {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return new Intl.DateTimeFormat("it-IT", { dateStyle: "short" }).format(date);
}

function formatDateTime(value?: string | null) {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return new Intl.DateTimeFormat("it-IT", { dateStyle: "short", timeStyle: "short" }).format(date);
}

function formatAmount(value: unknown, currency = "EUR") {
  if (value === null || value === undefined || value === "") return "—";
  const num = Number(value);
  if (Number.isNaN(num)) return String(value);
  return new Intl.NumberFormat("it-IT", { style: "currency", currency }).format(num);
}

export default function ReceivedInvoiceDetailPage() {
  const { id } = useParams<{ id: string }>();
  const { token } = useAuth() as any;

  const [item, setItem] = useState<ReceivedInvoiceDetail | null>(null);
  const [loading, setLoading] = useState(false);
  const [alert, setAlert] = useState<{
    type: "success" | "danger" | "warning" | "info";
    text: string;
  } | null>(null);

  const loadItem = useCallback(async () => {
    if (!id) return;
    try {
      setLoading(true);
      setAlert(null);
      const data = await electronicInvoicingApi.getPassiveReceivedInvoice(token, id);
      setItem(data || null);
    } catch (error: any) {
      setAlert({
        type: "danger",
        text: error?.message || "Errore nel caricamento dettaglio documento ricevuto",
      });
    } finally {
      setLoading(false);
    }
  }, [id, token]);

  useEffect(() => {
    loadItem();
  }, [loadItem]);

  async function downloadXml() {
    if (!item?.id || !item?.xmlFileName) return;
    try {
      const res = await fetch(`${API_BASE}/received-invoices/${encodeURIComponent(item.id)}/download-xml`, {
        method: "GET",
        credentials: "include",
        headers: token ? { Authorization: `Bearer ${token}` } : undefined,
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = item.xmlFileName || `${item.id}.xml`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch {
      setAlert({ type: "danger", text: "Errore durante download XML" });
    }
  }

  return (
    <div className="container py-4">
      <div className="d-flex flex-column flex-lg-row align-items-lg-center justify-content-between gap-3 mb-4">
        <div>
          <div className="mb-2">
            <Link to="/received-invoices" className="text-decoration-none">
              ← Torna alle fatture ricevute
            </Link>
          </div>
          <h1 className="h3 mb-1">Dettaglio documento ricevuto {item?.documentNumber ? `- ${item.documentNumber}` : ""}</h1>
          <p className="text-muted mb-0">Fornitore, XML, metadati e notifiche del documento passivo.</p>
        </div>
        <div className="d-flex gap-2">
          <button type="button" className="btn btn-outline-secondary" onClick={loadItem} disabled={loading}>
            {loading ? "Aggiornamento..." : "Aggiorna"}
          </button>
          <button type="button" className="btn btn-primary" onClick={downloadXml} disabled={!item?.xmlFileName}>
            Download XML
          </button>
        </div>
      </div>

      {alert && (
        <div className={`alert alert-${alert.type}`} role="alert">
          {alert.text}
        </div>
      )}

      {loading && !item ? (
        <div className="card border-0 shadow-sm">
          <div className="card-body py-5 text-center text-muted">Caricamento dettaglio documento...</div>
        </div>
      ) : !item ? (
        <div className="card border-0 shadow-sm">
          <div className="card-body py-5 text-center text-muted">Documento non trovato.</div>
        </div>
      ) : (
        <div className="row g-4">
          <div className="col-12 col-xl-4">
            <div className="card border-0 shadow-sm mb-4">
              <div className="card-body">
                <h2 className="h5 mb-3">Informazioni documento</h2>
                <div className="mb-2">
                  <strong>Numero:</strong> {item.documentNumber || "—"}
                </div>
                <div className="mb-2">
                  <strong>Tipo:</strong> {item.documentType || "—"}
                </div>
                <div className="mb-2">
                  <strong>Data:</strong> {formatDate(item.documentDate)}
                </div>
                <div className="mb-2">
                  <strong>Totale:</strong> {formatAmount(item.total, item.currency || "EUR")}
                </div>
                <div className="mb-2">
                  <strong>Stato:</strong> {item.status || "—"}
                </div>
                <div className="mb-2">
                  <strong>Provider:</strong> {item.provider || "—"}
                </div>
                <div className="mb-2">
                  <strong>ID esterno:</strong> {item.externalId || "—"}
                </div>
                <div className="mb-0">
                  <strong>Importata il:</strong> {formatDateTime(item.importedAt)}
                </div>
              </div>
            </div>

            <div className="card border-0 shadow-sm">
              <div className="card-body">
                <h2 className="h5 mb-3">Fornitore</h2>
                <div className="mb-2">
                  <strong>Nome:</strong> {item.supplierName || "—"}
                </div>
                <div className="mb-2">
                  <strong>P.IVA:</strong> {item.supplierVat || "—"}
                </div>
                <div className="mb-0">
                  <strong>Codice fiscale:</strong> {item.supplierFiscalCode || "—"}
                </div>
              </div>
            </div>
          </div>

          <div className="col-12 col-xl-8">
            <div className="card border-0 shadow-sm mb-4">
              <div className="card-body">
                <div className="d-flex align-items-center justify-content-between mb-3">
                  <h2 className="h5 mb-0">Anteprima XML</h2>
                  <span className="small text-muted">{item.xmlFileName || "Nessun file XML"}</span>
                </div>
                <div className="bg-light border rounded p-3" style={{ maxHeight: 420, overflow: "auto" }}>
                  <pre className="mb-0 small" style={{ whiteSpace: "pre-wrap" }}>
                    {item.xmlContent || "XML non disponibile."}
                  </pre>
                </div>
              </div>
            </div>

            <div className="card border-0 shadow-sm mb-4">
              <div className="card-body">
                <h2 className="h5 mb-3">Storico notifiche</h2>
                <div className="table-responsive">
                  <table className="table table-hover align-middle">
                    <thead>
                      <tr>
                        <th>Data</th>
                        <th>Tipo</th>
                        <th>Stato</th>
                        <th>Messaggio</th>
                      </tr>
                    </thead>
                    <tbody>
                      {!item.notifications || item.notifications.length === 0 ? (
                        <tr>
                          <td colSpan={4} className="text-center text-muted py-4">
                            Nessuna notifica disponibile.
                          </td>
                        </tr>
                      ) : (
                        item.notifications.map((n) => (
                          <tr key={n.id}>
                            <td>{formatDateTime(n.receivedAt)}</td>
                            <td>{n.notificationType}</td>
                            <td>{n.status}</td>
                            <td>{n.message || "—"}</td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>

            <div className="card border-0 shadow-sm">
              <div className="card-body">
                <h2 className="h5 mb-3">Payload raw</h2>
                <pre className="bg-light border rounded p-3 small mb-0" style={{ maxHeight: 320, overflow: "auto" }}>
                  {JSON.stringify(item.payload || {}, null, 2)}
                </pre>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
